import React, { useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { StopCircle } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';
import { useNotesBackgroudTimerContext } from 'modules/NotesBackgroudTimer/context/NotesBackgroudTimerContext';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import { useNotesBackgroudTimer } from 'modules/NotesBackgroudTimer/hooks/useNotesBackgroudTimer';
import { useMessage } from 'modules/intl';
import { truncateMessage } from 'modules/notes/common/utils/note-utils';
import useBackgroundTimerValue from './useBackgroundTimerValue';

type StyleArgs = {
  pinned: boolean;
  isSubstituteUser: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    width: '100%',
    justifyContent: 'flex-start',
    borderBottom: `solid ${theme.palette.grey[300]} 1px`,
    alignItems: 'center',
    paddingLeft: ({ pinned }: StyleArgs) =>
      pinned ? theme.spacing(32) : theme.spacing(10),
    marginTop: ({ isSubstituteUser }: StyleArgs) =>
      isSubstituteUser ? theme.spacing(4) : theme.spacing(0),
    paddingRight: theme.spacing(10),
    height: theme.spacing(5.3),
    backgroundColor: theme.palette.inlineTimer.main,
    boxShadow: '0px 3px 6px #00000029',
    opacity: 1
  },
  timer: {
    color: theme.palette.error.dark,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    opacity: 0.86
  },
  stopCircleIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    color: theme.palette.error.main,
    opacity: 1
  },
  textWrapper: {
    opacity: 0.86,
    textAlign: 'left',
    whiteSpace: 'pre',
    marginLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row'
  },
  link: {
    color: theme.palette.repliconBlue.main,
    fontWeight: 'bold'
  }
}));

export const BackgroundTimerBanner: React.FC = () => {
  const { isSubstituteUser } = useSubstituteUser();
  const { pinned = false } = useSidePanelContext();
  const classes = useStyles({ pinned, isSubstituteUser });
  const {
    running: isBackgroundTimerRunning,
    signalMeta
  } = useNotesBackgroudTimerContext();
  const { onStop } = useNotesBackgroudTimer();
  const { fromattedTimerValue } = useBackgroundTimerValue();
  const emptyNoteMessage = useMessage('note.emptyNote');
  const timerLabel = useMessage('timeSignalActions.timer');
  const descriptionText = useMemo(() => {
    if (!signalMeta?.description || !signalMeta?.description.length)
      return emptyNoteMessage;
    return truncateMessage(signalMeta?.description, 38);
  }, [signalMeta?.description, emptyNoteMessage]);

  if (!isBackgroundTimerRunning) return null;

  return (
    <Stack
      data-testid="background-timer-banner"
      direction="row"
      className={classes.root}
    >
      <Typography
        data-testid="background-timer-id"
        variant="subtitle2"
        className={classes.timer}
      >
        {fromattedTimerValue}
      </Typography>
      <IconButton
        onClick={onStop}
        data-testid="background-timer-stop-btn"
        aria-label={timerLabel}
      >
        <Tooltip title={<FormattedMessage id="timeSignalActions.timer" />}>
          <StopCircle className={classes.stopCircleIcon} />
        </Tooltip>
      </IconButton>
      <Stack
        data-testid="background-timer-notes"
        className={classes.textWrapper}
      >
        <Typography variant="subtitle2">
          <FormattedMessage id="note.inlineTimer.backgroundTimerMessage" />
        </Typography>
        <Link
          data-testid="background-timer-link"
          href="#"
          variant="subtitle2"
          underline="none"
          className={classes.link}
        >
          {descriptionText}
        </Link>
      </Stack>
    </Stack>
  );
};

export default BackgroundTimerBanner;
